/**
 * S3 \u7279\u6b8a\u5757\u7cfb\u7edf \u2014\u2014 \u5b9e\u73b0\u81ea design/gdd/systems/S3-special-blocks.md\u3002
 *
 * \u672c\u5207\u7247\u4ec5\u542f\u7528\u7b2c 1 \u578b\u63a8\u8fdb\u5668\uff08LINE_4 \u2192 THRUSTER\uff09\uff0c
 * \u4f46\u56db\u578b\u7684\u67e5\u8be2\u63a5\u53e3\u5168\u90e8\u5b9e\u73b0\uff08\u4f9b P4 \u76f4\u63a5\u5f00\u653e\uff09\u3002
 *
 * \u6838\u5fc3\u5951\u7ea6\uff1a
 *   FC-1\uff1a\u4efb\u610f\u4e00\u65b9\u542b SPECIAL \u5219\u4ea4\u6362\u5373\u5f15\u7206\uff08isSwapDetonatable \u6052 true\uff09
 *   FC-2\uff1a\u524d\u4e09\u578b\u7ee7\u627f\u89e6\u53d1\u8272\u3001\u53c2\u4e0e\u540c\u8272\u5339\u914d\uff1b\u661f\u4e91\u6838\u65e0\u8272\u3001\u4e0d\u53c2\u4e0e\u5339\u914d
 */

import {
  BOARD_SIZE, SHAPE_TO_SPECIAL,
  type Board0, type Coord, type Cell, type MatchGroup, type PieceColor, type ShapeType, type SpecialType,
  type Direction,
} from './types';
import { coordKey, inBoard } from './types';
import { getCell } from './board';

export interface GenerationRequest {
  group: MatchGroup;
  trigger: 'swap' | 'cascade';
  /** \u843d\u70b9\u683c\uff08\u73a9\u5bb6\u64cd\u4f5c\u683c / \u7ec4\u51e0\u4f55\u4e2d\u5fc3\uff09 */
  spawnCell: Coord;
  type: SpecialType;
}

export interface DetonationRequest {
  source: Coord;
  from: Coord;
  direction?: Direction;
  targetColor?: PieceColor;
}

export interface DetonationResult {
  clearedCells: Coord[];
  cascadeTriggered: boolean;
}

/** \u56db\u578b\u4f18\u5148\u7ea7\uff08S3 \u00a72.5 \u88c1\u51b3\uff09 */
export const SPECIAL_PRIORITY: readonly SpecialType[] = [
  'NEBULA_CORE', 'BLACK_HOLE', 'METEOR_BURST', 'THRUSTER',
] as const;

/** FC-1\uff1a\u63a5\u53e3\u6052\u4e3a true\uff08\u56db\u578b\u5168\u90e8\u652f\u6301\u4ea4\u6362\u5373\u5f15\u7206\uff09 */
export function isSwapDetonatable(_t: SpecialType): boolean {
  void _t;
  return true;
}

export function getSpecialType(cell: Cell | null): SpecialType | null {
  if (!cell || cell.kind !== 'SPECIAL') return null;
  return cell.special ?? null;
}

/** \u5f62\u72b6 \u2192 \u7279\u6b8a\u5757\u7c7b\u578b\uff08\u4e0d\u652f\u6301\u7684\u5f62\u72b6\u8fd4\u56de null\uff09 */
export function shapeToSpecial(shape: ShapeType): SpecialType | null {
  return SHAPE_TO_SPECIAL[shape] ?? null;
}

/** \u4ece\u591a\u4e2a\u5f62\u72b6\u63d0\u793a\u4e2d\u9009\u51fa\u6700\u9ad8\u4f18\u5148\u7ea7 */
export function pickHighestShape(shapes: ShapeType[]): ShapeType | null {
  for (const s of SPECIAL_PRIORITY) {
    const target = Object.entries(SHAPE_TO_SPECIAL).find(([, v]) => v === s)?.[0] as ShapeType | undefined;
    if (target && shapes.includes(target)) return target;
  }
  return null;
}

/**
 * S3 \u00a72.4\uff1a\u8ba1\u7b97\u7279\u6b8a\u5757\u751f\u6210\u843d\u70b9\u3002
 *   \u73a9\u5bb6\u4ea4\u6362\u89e6\u53d1 \u2192 \u73a9\u5bb6\u64cd\u4f5c\u683c\uff08\u4e24\u683c\u5747\u5728\u7ec4\u5185\u53d6 from\uff09
 *   \u8fde\u9501\u89e6\u53d1 \u2192 \u7ec4\u51e0\u4f55\u4e2d\u5fc3
 */
export function computeSpawnCell(
  group: MatchGroup,
  trigger: 'swap' | 'cascade',
  swapFrom?: Coord,
): Coord {
  if (trigger === 'swap' && swapFrom) {
    const inGroup = group.cells.some((p) => coordEq(p, swapFrom));
    if (inGroup) return swapFrom;
  }
  return geometricCenter(group.cells);
}

function coordEq(a: Coord, b: Coord): boolean {
  return a.c === b.c && a.r === b.r;
}

/** \u7ec4\u51e0\u4f55\u4e2d\u5fc3\uff08\u53d6\u6700\u63a5\u8fd1\u8d28\u5fc3\u7684\u5b9e\u683c\uff09 */
function geometricCenter(cells: Coord[]): Coord {
  if (cells.length === 0) return { c: 0, r: 0 };
  let sc = 0, sr = 0;
  for (const p of cells) { sc += p.c; sr += p.r; }
  const mc = sc / cells.length, mr = sr / cells.length;
  let best = cells[0], bestD = Infinity;
  for (const p of cells) {
    const d = (p.c - mc) ** 2 + (p.r - mr) ** 2;
    if (d < bestD) { bestD = d; best = p; }
  }
  return best;
}

/**
 * S3 \u00a72.6\uff1a\u8ba1\u7b97\u5f15\u7206\u96c6\u5408\u3002
 * \u53ef\u6d88\u9664\u683c = PIECE / SPECIAL\uff1bOBSTACLE \u4e0d\u53ef\u88ab\u5f15\u7206\u6d88\u9664\u3002
 */
export function computeDetonation(board: Board0, req: DetonationRequest): DetonationResult {
  const cell = getCell(board, req.source);
  const type = getSpecialType(cell);
  if (!type) return { clearedCells: [], cascadeTriggered: false };

  const cleared: Coord[] = [];
  const push = (p: Coord) => {
    if (!inBoard(p)) return;
    const t = getCell(board, p);
    if (!t) return;
    if (t.kind !== 'PIECE' && t.kind !== 'SPECIAL') return;
    cleared.push(p);
  };

  switch (type) {
    case 'THRUSTER': {
      // \u6574\u884c\u6216\u6574\u5217\uff08\u65b9\u5411 = \u4ea4\u6362\u65b9\u5411\uff09\u3002E14\uff1a\u88ab\u5339\u914d\u6d88\u9664\u65f6\u53d6\u7ec4\u4e3b\u65b9\u5411
      const dir = req.direction ?? 'right';
      if (dir === 'left' || dir === 'right') {
        for (let c = 0; c < BOARD_SIZE; c++) push({ c, r: req.source.r });
      } else {
        for (let r = 0; r < BOARD_SIZE; r++) push({ c: req.source.c, r });
      }
      break;
    }
    case 'METEOR_BURST': {
      // 3\u00d73
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          push({ c: req.source.c + dc, r: req.source.r + dr });
        }
      }
      break;
    }
    case 'BLACK_HOLE': {
      // \u5168\u5c4f\u81ea\u8eab\u8272\uff08\u7ee7\u627f\u4e8e\u89e6\u53d1\u65f6\u7684\u989c\u8272\uff09
      const target = cell?.color;
      if (!target) break;
      for (let r = 0; r < BOARD_SIZE; r++) {
        for (let c = 0; c < BOARD_SIZE; c++) {
          const t = getCell(board, { c, r });
          if (t?.color === target && (t.kind === 'PIECE' || t.kind === 'SPECIAL')) {
            push({ c, r });
          }
        }
      }
      break;
    }
    case 'NEBULA_CORE': {
      // \u5168\u5c4f\u4ea4\u6362\u5bf9\u8c61\u8272
      const target = req.targetColor;
      if (!target) break;
      for (let r = 0; r < BOARD_SIZE; r++) {
        for (let c = 0; c < BOARD_SIZE; c++) {
          const t = getCell(board, { c, r });
          if (t?.color === target && (t.kind === 'PIECE' || t.kind === 'SPECIAL')) {
            push({ c, r });
          }
        }
      }
      break;
    }
  }

  // \u53bb\u91cd
  const seen = new Set<string>();
  const unique = cleared.filter((p) => {
    const k = coordKey(p);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  return { clearedCells: unique, cascadeTriggered: true };
}

/** \u5728\u76d8\u9762\u4e0a\u653e\u7f6e\u4e00\u679a\u7279\u6b8a\u5757 */
export function placeSpecial(
  board: Board0,
  cell: Coord,
  type: SpecialType,
  color?: PieceColor,
): boolean {
  if (!inBoard(cell)) return false;
  const target = board.cells[cell.r][cell.c];
  if (target.kind === 'OBSTACLE') return false; // \u5360\u7528\u683c\u4e0d\u53e0\u653e
  board.cells[cell.r][cell.c] = {
    kind: 'SPECIAL',
    special: type,
    color: type === 'NEBULA_CORE' ? undefined : color,
  };
  return true;
}
